import type { server } from '@arena/server';
import { RoomName, type CreateOptions, type JoinOptions } from '@arena/shared';
import { Client, type Room } from '@colyseus/sdk';

type ServerRooms = (typeof server)['~rooms'];
export type ArenaRoom = Room<ServerRooms['arena']['~room']>;
export type LobbyRoom = Room<ServerRooms['lobby']['~room']>;
export type ArenaState = ArenaRoom['state'];
export type PlayerState = NonNullable<ReturnType<ArenaState['players']['get']>>;

function defaultServerUrl(): string {
  const secure = window.location.protocol === 'https:';
  return `${secure ? 'wss' : 'ws'}://${window.location.hostname}:2567`;
}

/** Typed end to end against the server definition (room names, state, messages, input). */
export const client: Client<typeof server> = new Client<typeof server>(
  import.meta.env.VITE_SERVER_URL ?? defaultServerUrl(),
);

export const joinOrCreateArena = (options: JoinOptions): Promise<ArenaRoom> =>
  client.joinOrCreate(RoomName.Arena, options);
export const createArena = (options: CreateOptions): Promise<ArenaRoom> =>
  client.create(RoomName.Arena, options);
export const joinArenaById = (roomId: string, options: JoinOptions): Promise<ArenaRoom> =>
  client.joinById(roomId, options);
// Not `reconnect(token, 'arena')`: the SDK's typed room-name overload throws at runtime in 0.18.
export const reconnectArena = (token: string): Promise<ArenaRoom> =>
  client.reconnect<ServerRooms['arena']['~room']>(token);
/** The lobby room is created on demand by the first browser that opens the home screen. */
export const joinLobby = (): Promise<LobbyRoom> => client.joinOrCreate(RoomName.Lobby);
